import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, filter, finalize, switchMap, take, tap, throwError } from 'rxjs';
import { ToastService } from '../services/toast.service';
import { TokenService } from '../services/token.service';

// Peticiones que muestran su propio estado en pantalla: no disparan toasts de éxito ni de
// error. El refresh de sesión (401) sí aplica.
export const SILENT_REQUEST = new HttpContextToken<boolean>(() => false);

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

function extractServerMessage(error: HttpErrorResponse): string | null {
  let body = error.error;
  // Los endpoints que responden NoContent en éxito piden responseType 'text' — en un error,
  // Angular entonces NO parsea el body como JSON (llega como string crudo). Sin esto, el toast
  // muestra el JSON tal cual en vez del mensaje.
  if (typeof body === 'string' && body.trim().startsWith('{')) {
    try { body = JSON.parse(body); } catch { /* no era JSON, se deja como string */ }
  }
  if (typeof body?.message === 'string' && body.message) return body.message;
  if (typeof body?.error === 'string' && body.error) return body.error;
  if (typeof body === 'string' && body) return body;
  return null;
}

function handle401(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  tokenService: TokenService,
  toast: ToastService,
  router: Router
): Observable<HttpEvent<unknown>> {
  if (isRefreshing) {
    return refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) =>
        next(request.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))
      )
    );
  }

  isRefreshing = true;
  refreshTokenSubject.next(null);

  return tokenService.refresh().pipe(
    catchError((err: HttpErrorResponse) => {
      tokenService.clearTokens();
      const msg = extractServerMessage(err) ?? 'Tu sesión ha expirado. Inicia sesión de nuevo.';
      toast.showError(msg, 'Sesión expirada');
      router.navigate(['/auth/login']);
      return throwError(() => err);
    }),
    finalize(() => {
      isRefreshing = false;
    }),
    switchMap((newToken) => {
      refreshTokenSubject.next(newToken);
      return next(request.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }));
    })
  );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const toast = inject(ToastService);
  const router = inject(Router);

  const token = tokenService.getToken();
  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  const MUTATION_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];
  const SUCCESS_STATUSES = [HttpStatusCode.Ok, HttpStatusCode.Created, HttpStatusCode.Accepted];
  const silent = req.context.get(SILENT_REQUEST);

  return next(authReq).pipe(
    tap((event) => {
      if (
        !silent &&
        event instanceof HttpResponse &&
        SUCCESS_STATUSES.includes(event.status) &&
        MUTATION_METHODS.includes(authReq.method)
      ) {
        const body = event.body as { message?: string } | null;
        if (body?.message) {
          toast.showSuccess(body.message);
        }
      }
    }),
    catchError((error) => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === HttpStatusCode.Unauthorized) {
          return handle401(authReq, next, tokenService, toast, router);
        }
        if (silent) return throwError(() => error);

        if (error.status === 0) {
          toast.showError('No hay conexión. Verifica tu red e intenta de nuevo.');
        } else {
          const msg = extractServerMessage(error) ?? 'Error en la solicitud. Intenta de nuevo.';
          toast.showError(msg);
        }
      } else if (silent) {
        return throwError(() => error);
      } else if (error?.name === 'TimeoutError') {
        toast.showError('El servidor tardó demasiado en responder. Intenta de nuevo.');
      } else {
        toast.showError('Error en la solicitud. Intenta de nuevo.');
      }

      return throwError(() => error);
    })
  );
};
